import type { PostHog } from "posthog-js";
import type {
  AnalyticsIntegration,
  IntegrationAccountGroup,
  IntegrationConsent,
  IntegrationIdentity,
  IntegrationProperties,
} from "@/shared/integrations/integration.types";

// reason: `import.meta.env` is undefined under node:test + tsx. Optional
// chaining keeps this adapter importable without configuring browser globals.
const POSTHOG_KEY = (import.meta.env?.VITE_POSTHOG_PROJECT_TOKEN ?? "").trim();
const DEFAULT_POSTHOG_HOST = "https://us.i.posthog.com";
const POSTHOG_HOST = (import.meta.env?.VITE_POSTHOG_HOST ?? "").trim() || DEFAULT_POSTHOG_HOST;
const MAX_QUEUED_TASKS = 50;

export const isPostHogConfigured = POSTHOG_KEY.length > 0;

type PostHogTask = (posthog: PostHog) => void;

let consent: IntegrationConsent = "unknown";
let client: PostHog | null = null;
let load: Promise<PostHog | null> | null = null;
const queued: PostHogTask[] = [];

export function shouldResetBeforeIdentify(
  isIdentified: boolean,
  distinctId: string,
  userId: string,
): boolean {
  return isIdentified && distinctId !== userId;
}

function loadClient(): Promise<PostHog | null> {
  if (!isPostHogConfigured || consent !== "granted") return Promise.resolve(null);
  if (load) return load;

  load = import("posthog-js")
    .then(({ default: posthog }) => {
      posthog.init(POSTHOG_KEY, {
        api_host: POSTHOG_HOST,
        defaults: "2026-05-30",
        persistence: "localStorage+cookie",
        cross_subdomain_cookie: true,
        cookieWinsOnConflict: true,
        opt_out_capturing_by_default: true,
        autocapture: false,
        disable_session_recording: true,
        capture_dead_clicks: false,
        capture_heatmaps: false,
        capture_performance: false,
        capture_exceptions: false,
        disable_surveys: true,
        capture_pageview: false,
        capture_pageleave: true,
        person_profiles: "identified_only",
      });
      client = posthog;
      if (consent === "granted") posthog.opt_in_capturing({ captureEventName: false });
      else posthog.opt_out_capturing();
      return posthog;
    })
    .catch(() => null);

  return load;
}

function run(task: PostHogTask): void {
  if (!isPostHogConfigured || consent !== "granted") return;
  if (client) {
    task(client);
    return;
  }

  if (queued.length < MAX_QUEUED_TASKS) queued.push(task);
  void loadClient().then((posthog) => {
    if (!posthog || consent !== "granted") {
      queued.length = 0;
      return;
    }
    while (queued.length > 0) queued.shift()?.(posthog);
  });
}

function setConsent(next: IntegrationConsent): void {
  consent = next;
  if (next === "granted") {
    if (client) {
      client.opt_in_capturing({ captureEventName: false });
      return;
    }
    void loadClient();
    return;
  }

  queued.length = 0;
  if (next === "denied" && client) {
    client.reset();
    client.opt_out_capturing();
  }
}

async function reset(): Promise<void> {
  queued.length = 0;
  if (!isPostHogConfigured || consent !== "granted") return;
  const posthog = client ?? (await loadClient());
  posthog?.reset();
}

function identify(identity: IntegrationIdentity): void {
  run((posthog) => {
    if (
      shouldResetBeforeIdentify(posthog._isIdentified(), posthog.get_distinct_id(), identity.userId)
    ) {
      posthog.reset();
    }
    posthog.identify(identity.userId, {
      email: identity.email,
      name: identity.name,
      account_id: identity.accountId,
      account_role: identity.accountRole,
      workspace_id: identity.workspaceId,
      ...(identity.plan ? { current_plan: identity.plan } : {}),
    });
  });
}

function groupAccount(group: IntegrationAccountGroup): void {
  run((posthog) => {
    posthog.group("account", group.accountId, {
      name: group.name,
      plan: group.plan,
      seats_used: group.seatsUsed,
      seat_cap: group.seatCap,
    });
  });
}

function capture(name: string, properties?: IntegrationProperties): void {
  run((posthog) => posthog.capture(name, properties));
}

export const postHogIntegration: AnalyticsIntegration = {
  id: "posthog",
  category: "analytics",
  setConsent,
  reset,
  capture,
  identify,
  groupAccount,
};
